import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Mark } from '@/components/brand/Brand'
import { useIsClient, useMotion } from '@/components/motion/motion'
import { cx } from '@/lib/cx'

/* How the transformation works: five steps on the left, and a sticky map of "Your firm" on the
   right that follows the step at the reading line. Map, Measure, Automate (the books), Repeat (every
   remaining workflow, one after another), Keep it (shared with every partner firm). */

const steps = [
  {
    title: 'Map',
    text: 'We write down every workflow in your firm: how you collect documents and data from clients, how you keep the books and close the month, how you prepare tax, how you answer clients, how you run the firm.',
  },
  { title: 'Measure', text: 'We find where the hours actually go, by person and by client.' },
  {
    title: 'Automate the biggest bottleneck first',
    text: 'Our agents take on that work on your clients’ books, and your reviewers approve every output. If part of your production runs offshore, that is the first work to move.',
  },
  { title: 'Repeat', text: 'Next bottleneck, same method.' },
  { title: 'Keep it', text: 'Improvements we make with your firm become part of Justine for every partner firm.' },
]

const stepLabels = ['', 'Step 1 · Map', 'Step 2 · Measure', 'Step 3 · Automate', 'Step 4 · Repeat', 'Step 5 · Keep it']

type FlowKey = 'docs' | 'books' | 'tax' | 'clients' | 'run'

/** Each workflow, with its share of the team's hours. */
const flows: Array<{ key: FlowKey; name: string; hours: string }> = [
  { key: 'docs', name: 'Collecting documents and data', hours: '70%' },
  { key: 'books', name: 'Keeping the books and closing the month', hours: '94%' },
  { key: 'tax', name: 'Preparing tax', hours: '54%' },
  { key: 'clients', name: 'Answering clients', hours: '42%' },
  { key: 'run', name: 'Running the firm', hours: '30%' },
]

/** The order the remaining workflows move over in at the Repeat step. */
const repeatOrder: FlowKey[] = ['docs', 'tax', 'clients', 'run']

const isAutomated = (step: number, flow: FlowKey) => (flow === 'books' ? step >= 3 : step >= 4)

const tagFor = (step: number, flow: FlowKey): [text: string, tone?: 'big' | 'auto'] => {
  if (step === 1) return ['Mapped']
  if (step === 2) return flow === 'books' ? ['Most hours', 'big'] : ['']
  if (isAutomated(step, flow)) return step === 5 ? ['In Justine', 'auto'] : ['Agents · you approve', 'auto']
  return ['']
}

const tagStepsAt = (step: number) => Object.fromEntries(flows.map(({ key }) => [key, step])) as Record<FlowKey, number>

export const MethodSection = () => {
  const { paused, reduceMotion } = useMotion()
  const isClient = useIsClient()
  const stepsRef = useRef<HTMLOListElement>(null)
  const motion = useRef({ paused, reduceMotion })
  const [step, setStep] = useState(1)
  const [staggered, setStaggered] = useState(false)
  // Tags can trail the step while the Repeat step staggers.
  const [tagSteps, setTagSteps] = useState(() => tagStepsAt(1))

  useEffect(() => {
    motion.current = { paused, reduceMotion }
  }, [paused, reduceMotion])

  useEffect(() => {
    const list = stepsRef.current
    if (!list) return
    const headings = Array.from(list.children).map((item) => item.firstElementChild)

    let current = 1
    let frame = 0
    const tagTimers: number[] = []

    const goTo = (next: number) => {
      if (next === current) return
      const stagger = next === 4 && current < 4 && !motion.current.paused && !motion.current.reduceMotion
      current = next
      tagTimers.splice(0).forEach((timer) => window.clearTimeout(timer))
      setStep(next)
      setStaggered(stagger)
      if (!stagger) {
        setTagSteps(tagStepsAt(next))
        return
      }
      setTagSteps((tags) => ({ ...tags, books: next }))
      repeatOrder.forEach((flow, index) => {
        tagTimers.push(window.setTimeout(() => setTagSteps((tags) => ({ ...tags, [flow]: next })), index * 350))
      })
    }

    // The step whose heading has passed the reading line, a little above the middle, leads.
    const pick = () => {
      frame = 0
      if (list.offsetParent === null) return
      const line = (window.innerHeight || 800) * 0.55
      let best = 1
      headings.forEach((heading, index) => {
        if (heading && heading.getBoundingClientRect().top <= line) best = index + 1
      })
      goTo(best)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(pick)
    }

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    schedule()
    return () => {
      cancelAnimationFrame(frame)
      tagTimers.forEach((timer) => window.clearTimeout(timer))
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [])

  return (
    <section id="w-method" className={cx('w-sec w-method', isClient && 'w-live')} aria-labelledby="w-method-title">
      <div className="x-wrap">
        <div className="w-head">
          <p className="x-eyebrow x-eyebrow--ink">The transformation</p>
          <h2 id="w-method-title" className="x-h2">
            How the transformation works
          </h2>
        </div>
        <div className="w-method-grid">
          <ol ref={stepsRef} className="w-steps-list">
            {steps.map((item, index) => (
              <li key={item.title} className={cx('w-step', index === step - 1 && 'is-active')}>
                <p className="x-ch-n">
                  <span>{`0${index + 1}`}</span>Step
                </p>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ol>

          <div className="w-visual" aria-hidden="true">
            <div
              className={cx('w-map', step >= 2 && 's2', step >= 3 && 's3', step >= 4 && 's4', step >= 5 && 's5')}
              data-step={step}
            >
              <div className="w-map-head">
                <p className="w-map-title">Your firm</p>
                <p className="w-map-state">{stepLabels[step]}</p>
              </div>
              <ol className="w-flows">
                {flows.map((flow) => {
                  const [tag, tone] = tagFor(tagSteps[flow.key], flow.key)
                  const delay = staggered && flow.key !== 'books' ? repeatOrder.indexOf(flow.key) * 0.35 : 0
                  return (
                    <li
                      key={flow.key}
                      className={cx('w-flow', isAutomated(step, flow.key) && 'auto', flow.key === 'books' && step === 2 && 'big')}
                      style={{ '--w': flow.hours, '--d': `${delay}s` } as CSSProperties}
                    >
                      <span className="w-flow-name">{flow.name}</span>
                      <span className={cx('w-flow-tag', tone && `t-${tone}`)}>{tag || null}</span>
                      <span className="w-flow-bar">
                        <i className="h" />
                        <i className="a" />
                      </span>
                    </li>
                  )
                })}
              </ol>
              <div className="w-legend">
                <span className="k-h">Team hours</span>
                <span className="k-a">Agents prepare</span>
                <span className="k-r">Your reviewers approve</span>
              </div>
              <div className="w-shelf">
                <span className="w-shelf-mark">
                  <Mark />
                  Justine
                </span>
                <span className="w-shelf-text">For every partner firm</span>
                <span className="w-firms">
                  {Array.from({ length: 6 }, (_, index) => (
                    <i key={index} />
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
